import React, { useCallback, useEffect, useState } from 'react';
import { Download, FileWarning, Loader2 } from 'lucide-react';
import { inquiriesAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';

export const InquiryDocumentPreviewDialog = ({ inquiryId, document, open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setPreview(null);
    setActiveSheet(0);
    try {
      const response = await inquiriesAPI.previewDocument(inquiryId, document.id);
      setPreview(response.data);
      if (response.data.kind === 'inline') {
        const blob = await inquiriesAPI.downloadDocument(inquiryId, document.id, response.data.converted);
        const typed = new Blob([blob.data], { type: response.data.mime_type || 'application/octet-stream' });
        setObjectUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return URL.createObjectURL(typed);
        });
      }
    } catch (error) {
      setPreview({ kind: 'error', message: error.response?.data?.detail || 'Preview is not available for this file' });
    } finally {
      setLoading(false);
    }
  }, [inquiryId, document]);

  useEffect(() => {
    if (open && document) load();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document?.id]);

  const download = async () => {
    try {
      const response = await inquiriesAPI.downloadDocument(inquiryId, document.id);
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement('a');
      anchor.href = url; anchor.download = document.filename; anchor.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (!document) return null;
  const sheet = preview?.sheets?.[activeSheet];
  const isImage = (preview?.mime_type || '').startsWith('image/');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[96vw] max-w-[1400px] overflow-hidden p-0" data-testid="inquiry-document-preview-dialog">
        <DialogHeader className="flex flex-row items-center justify-between gap-4 border-b px-6 py-4">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base" data-testid="inquiry-document-preview-title">{document.filename}</DialogTitle>
            <DialogDescription className="text-xs">Read-only preview of the retained source file.</DialogDescription>
          </div>
          <Button size="sm" variant="outline" onClick={download} data-testid="inquiry-document-preview-download">
            <Download className="mr-2 h-4 w-4" /> Download
          </Button>
        </DialogHeader>
        <div className="max-h-[78vh] overflow-auto px-6 py-5">
          {loading && (
            <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="inquiry-document-preview-loading">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading preview…
            </div>
          )}
          {!loading && preview?.kind === 'table' && sheet && (
            <div className="space-y-4" data-testid="inquiry-document-preview-table">
              {preview.sheets.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {preview.sheets.map((item, index) => (
                    <Button key={item.name} size="sm" variant={index === activeSheet ? 'default' : 'outline'}
                      onClick={() => setActiveSheet(index)} data-testid={`preview-sheet-tab-${index}`}>{item.name}</Button>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {sheet.total_rows} row(s){sheet.truncated ? ' — showing first 500' : ''}
              </p>
              <div className="overflow-x-auto border">
                <Table>
                  <TableHeader><TableRow>{sheet.columns.map((column) => <TableHead key={column} className="whitespace-nowrap">{column}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {sheet.rows.map((row, rowIndex) => (
                      <TableRow key={`row-${rowIndex}`} data-testid={`preview-row-${rowIndex}`}>
                        {row.map((value, cellIndex) => <TableCell key={`cell-${rowIndex}-${cellIndex}`} className="whitespace-nowrap text-sm">{value}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          {!loading && preview?.kind === 'inline' && objectUrl && (
            isImage ? (
              <img src={objectUrl} alt={document.filename} className="mx-auto max-h-[72vh] w-auto" data-testid="inquiry-document-preview-image" />
            ) : (
              <iframe src={objectUrl} title={document.filename} className="h-[72vh] w-full border" data-testid="inquiry-document-preview-frame" />
            )
          )}
          {!loading && (preview?.kind === 'unsupported' || preview?.kind === 'error') && (
            <div className="py-20 text-center" data-testid="inquiry-document-preview-unsupported">
              <FileWarning className="mx-auto mb-3 h-9 w-9 text-amber-600" />
              <p className="font-medium">{preview.kind === 'error' ? preview.message : 'This file type cannot be shown in the browser'}</p>
              <p className="mt-1 text-sm text-muted-foreground">Download the original file to open it in its native application.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
